"use client";

import { Heart, ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
const donationAmounts = [{
  label: "£10",
  url: "https://donate.stripe.com/9B600j7Wq8e1eZN02bb3q00"
}, {
  label: "£25",
  url: "https://donate.stripe.com/3cI00j1y2fGtaJxcOXb3q01"
}, {
  label: "£50",
  url: "https://donate.stripe.com/6oU00j2C62TH6th02bb3q02",
  featured: true
}, {
  label: "£100",
  url: "https://donate.stripe.com/6oUeVdgsW79XdVJbKTb3q03"
}, {
  label: "£250",
  url: "https://donate.stripe.com/3cI8wPa4y2THbNB6qzb3q04"
}, {
  label: "Other Amount",
  url: "https://donate.stripe.com/14A8wP7Wqam9g3R6qzb3q05"
}];
const impactPoints = ["Support children's education in Ghana", "Help fund ICT access and practical community projects", "Give securely and easily through Stripe"];
export default function Donation() {
  return <section data-section-id="96646" id="giving-options" className="bg-background py-12 sm:py-16 md:py-20 lg:py-24 text-foreground">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-12">
            <div className="max-w-2xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-secondary/10 px-4 py-2 text-sm font-semibold text-secondary">
                <Heart className="h-4 w-4" />
                Give Hope. Build Futures.
              </div>

              <h2 className="font-serif text-4xl font-bold leading-tight text-primary sm:text-5xl lg:text-6xl">
                Every donation helps change a life
              </h2>

              <p className="mt-5 text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
                Your support helps us improve education and opportunity for
                children and communities in Ghana. Every gift, no matter the
                amount, helps us continue practical projects that make a real
                difference.
              </p>

              <div className="mt-6 space-y-3">
                {impactPoints.map((point, i) => <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
                    <p className="text-sm text-foreground/85 sm:text-base">
                      {point}
                    </p>
                  </div>)}
              </div>

              <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-secondary" />
                Safe and secure donations powered by Stripe
              </div>
            </div>

            <div>
              <Card className="overflow-hidden rounded-3xl border border-primary/10 bg-card shadow-xl">
                <CardContent className="p-6 sm:p-8 lg:p-10">
                  <div className="mb-6 text-center">
                    <h3 className="font-serif text-2xl font-bold text-primary sm:text-3xl">
                      Choose an amount
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                      Select an amount below to donate securely with Stripe.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
                    {donationAmounts.map(amount => <a key={amount.label} href={amount.url} className={`group relative flex h-14 items-center justify-center rounded-2xl border text-base font-bold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${amount.featured ? "border-transparent bg-secondary text-secondary-foreground shadow-md hover:bg-secondary/90 hover:shadow-lg" : "border-primary/15 bg-background text-primary hover:border-primary/30 hover:bg-primary/5 hover:shadow-sm"}`} aria-label={`Donate ${amount.label}`}>
                        {amount.featured && <span className="absolute -top-2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
                            Popular
                          </span>}
                        <span className="transition-transform duration-200 group-hover:scale-[1.03]">
                          {amount.label}
                        </span>
                      </a>)}
                  </div>

                  <div className="mt-6 rounded-2xl bg-primary/5 p-4 sm:p-5">
                    <p className="text-sm leading-7 text-foreground/85 sm:text-base">
                      <span className="font-bold text-primary">Why give?</span>{" "}
                      Your donation supports projects such as ICT access,
                      school transport support, learning environments, and
                      future community initiatives.
                    </p>
                  </div>

                  <div className="mt-6">
                    <div className="mb-3 flex items-center justify-center gap-2 text-sm font-medium text-primary">
                      <ArrowRight className="h-4 w-4" />
                      Donate securely with Stripe
                    </div>

                    <a href="https://donate.stripe.com/14A8wP7Wqam9g3R6qzb3q05" className="flex h-14 w-full items-center justify-center rounded-full bg-primary text-base font-bold text-primary-foreground shadow-md transition-all duration-200 hover:bg-primary/90 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 sm:text-lg">
                      Donate Another Amount
                    </a>
                  </div>

                  <p className="mt-4 text-center text-xs text-muted-foreground sm:text-sm">
                    Every amount matters. Thank you for supporting our mission.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-5 sm:mt-16 md:grid-cols-3">
            <Card className="rounded-2xl border border-primary/10 bg-card shadow-sm">
              <CardContent className="p-6">
                <h3 className="mb-2 font-serif text-xl font-bold text-primary">
                  Quick to give
                </h3>
                <p className="text-sm leading-7 text-muted-foreground sm:text-base">
                  Donate in just a few clicks with a simple and secure Stripe
                  payment page.
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border border-primary/10 bg-card shadow-sm">
              <CardContent className="p-6">
                <h3 className="mb-2 font-serif text-xl font-bold text-primary">
                  Flexible giving
                </h3>
                <p className="text-sm leading-7 text-muted-foreground sm:text-base">
                  Choose a set amount or select another amount that works for
                  you.
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border border-primary/10 bg-card shadow-sm">
              <CardContent className="p-6">
                <h3 className="mb-2 font-serif text-xl font-bold text-primary">
                  Real impact
                </h3>
                <p className="text-sm leading-7 text-muted-foreground sm:text-base">
                  Your support helps fund practical education and community
                  development projects in Ghana.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}